import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from "react-router-dom";
import ReactDOM from 'react-dom';
import { useForm } from '@mantine/form';
import { Modal, Button, Group, Input, Space, Text } from '@mantine/core';
import { IconHelp, IconX, IconCheck } from '@tabler/icons-react';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import SettingsApi from '../../../api/SettingsApi';
import { SelectTooltip, VisiLoader } from "../../GeneralComponent/GeneralComponent";

export function DomainFlickerSettings({ websiteId, settingModalOpen, handleSettingCloseModal }:any) {
  const navigate = useNavigate();
  const [settingdata, setSettingData] = useState({ savepagetimeout: '',saveflickercontrol:'' });
  const [PageTimeout, setPageTimeout] = useState('');
  const [FlickerControl, setFlickerControl] = useState('');
  const [isVisiLoaderVisible, setIsVisiLoaderVisible] = useState(false);

  const settingsIconRef = useRef(null);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [helpTargetId, setHelpTargetId] = useState(0);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });

  const form = useForm({
    initialValues: {
      savepagetimeout: '',
      saveflickercontrol:''
    },
  });

  useEffect(() => {
    if(!websiteId) return;
    setIsVisiLoaderVisible(true);
    SettingsApi.getsitesettings({
      system_website_id: websiteId
    })
    .then((response) => {
      if (response.ok) {
        return response.json();
      } else {
        if (response.status === 401 || response.status === 400) {
          navigate('/logout');
          setIsVisiLoaderVisible(false);
          return null;
        }
      }
    })
    .then((data) => {
      if (data && data.status === 1) {
        setPageTimeout(data.data.site_timeout);
        setFlickerControl(data.data.site_flicker);
        setIsVisiLoaderVisible(false);
      } else if (data) {
        setIsVisiLoaderVisible(false);
        notifications.show({
          color: 'red',
          icon: <IconX size="1.1rem" />,
          title: 'Error',
          message: data.message || 'Failed to load Settings data',
        });
      }
    })
    .catch((error) => {
      console.error('API Error:', error);
      setIsVisiLoaderVisible(false);
      notifications.show({
        color: 'red',
        icon: <IconX size="1.1rem" />,
        title: 'Error',
        message: 'Failed to load Settings data.',
      });
    });
  }, [settingModalOpen]);

  const handleSavePageTimeoutSubmit = async (formData:any) => {
    setIsVisiLoaderVisible(true);
    SettingsApi.savesitesettings({
      system_website_id: websiteId,
      visiopt_timeout: settingdata.savepagetimeout,
      flicker_control: settingdata.saveflickercontrol
    })
    .then(response => {
      if (response.ok) {
        return response.json();
      } else {
        if (response.status === 401 || response.status === 400) {
          navigate("/logout");
          setIsVisiLoaderVisible(false);
        }
      }
    })
    .then(data => {
      if (data.status === 1) {
        if(data.data.msg === ""){
          notifications.show({
            color: 'green',
            icon: <IconCheck size="1.1rem" />,
            title: 'Success',
            message: data.message,
          });
          handleSettingCloseModal();
          setIsVisiLoaderVisible(false);
        } else {
          modals.open({
            title: 'Message',
            children: (
            <>
              <Text size="sm">
                {data.data.msg}
              </Text>
              <Group position="right" mt="md">
                <Button onClick={() => {
                  modals.closeAll();
                }}>OK</Button>
              </Group>
            </>
            ),
          });
          handleSettingCloseModal();
          setIsVisiLoaderVisible(false);
        }
      } else {
        form.setErrors({ savepagetimeout: data.message });
        form.setErrors({ saveflickercontrol: data.message });
        setIsVisiLoaderVisible(false);
      }
    })
    .catch(error => {
      console.error("API Error:", error);
      setIsVisiLoaderVisible(false);
    });
  }

  useEffect(() => {
    if (PageTimeout || FlickerControl) {
      setSettingData(prevSettingData => ({ ...prevSettingData, savepagetimeout: PageTimeout, saveflickercontrol:FlickerControl  }));
    }
  }, [PageTimeout,FlickerControl]);

  const handleChangePageTime = (e) => {
    setSettingData({ ...settingdata, savepagetimeout: e.target.value });
  };

  const handleChangeFlickerControl = (e) => {
    setSettingData({ ...settingdata, saveflickercontrol: e.target.value });
  };

  const handleHelpClick = (id:any) => {
    setShowHelpModal(true);
    setHelpTargetId(id);
    if (settingsIconRef.current) {
      const { top, left, width } = settingsIconRef.current.getBoundingClientRect();
      const scrollOffset = window.scrollY;
      setTooltipPosition({
        top: top + scrollOffset,
        left: left + width + 10, 
      });
    }
  };

  return (
    <>
      {showHelpModal &&
          ReactDOM.createPortal(
            <div>
              <SelectTooltip
                isVisible={showHelpModal}
                setIsVisible={setShowHelpModal}
                id={helpTargetId}
                tooltipPosition={tooltipPosition} 
              />
            </div>,
          document.body
      )}
      <Modal.Root opened={settingModalOpen} onClose={handleSettingCloseModal} trapFocus={false} style={{ zIndex: 2 }}>
        <Modal.Overlay />
        <Modal.Content>
          <VisiLoader VisiLoaderVisible={isVisiLoaderVisible} />
          <Modal.Header>
            <Modal.Title>SETTING <IconHelp ref={settingsIconRef} size="1.1rem" color="red" style={{marginBottom:-3, cursor: 'pointer'}} onClick={() => handleHelpClick(327)}/></Modal.Title>
            <Modal.CloseButton />
          </Modal.Header>
          <Modal.Body>
            <form onSubmit={form.onSubmit((formData) => { handleSavePageTimeoutSubmit(formData); })}>
              <Input.Wrapper label="Set Page Delay Timeout (in milliseconds)" error={form.errors.savepagetimeout}>
                <Input id="savepagetimeout" type="number" name="savepagetimeout" value={settingdata.savepagetimeout} hidden={PageTimeout ? true : false} onChange={handleChangePageTime}/>
              </Input.Wrapper>        
              <Input.Wrapper label="Flicker Control (in milliseconds)" error={form.errors.saveflickercontrol}>
                <Input id="saveflickercontrol" type="number" name="saveflickercontrol" value={settingdata.saveflickercontrol} hidden={FlickerControl ? true : false} onChange={handleChangeFlickerControl}/>
              </Input.Wrapper>
              <Space w="xs" />
              <Group position="right">
                <Button type="submit" size="sm" w={80} mt={25}>
                  Save
                </Button>
              </Group>
            </form>
          </Modal.Body>
        </Modal.Content>
      </Modal.Root>
    </>
  );
}